// Single source of truth for every design token (see DESIGN_SYSTEM.md).
// GlobalStyles turns these into CSS custom properties, and materialTheme
// feeds the same values to MUI, so the two can never drift.

export const colors = {
  // bone & charcoal: the neutrals carry the calm, the palette carries the punch
  paper: "#efe7da",
  paperDot: "#ddd1bd",
  card: "#fbf7f0",
  cardSunken: "#e8dfcf",
  ink: "#241e1c",
  inkSoft: "#574e48",
  inkFaint: "#6a615a",
  line: "#d8ccb8",
  lineStrong: "#bcab90",

  // the black card: burnt charcoal, a little warm
  night: "#1f1a19",
  nightSoft: "#3a3230",
  nightLip: "#0d0b0a",
  nightInk: "#f3ece0",

  // the palette: three loud colours and nothing else. fill / shade / tint
  gold: "#ffbe0b",
  goldShade: "#7a5200",
  goldTint: "#fff0c2",
  cherry: "#ff4d5e",
  cherryShade: "#a3122a",
  cherryTint: "#ffe0e3",
  teal: "#00b8a3",
  tealShade: "#00665b",
  tealTint: "#d3f5ef",

  warn: "#6b4700",
} as const;

// Seat colors offered in the avatar picker: one tone per hue around the whole
// wheel (primaries, secondaries and the steps between) plus charcoal. Every
// tone is dark enough that the white animal glyph from react-animals keeps at
// least 3.9:1 contrast on top of it.
export const PLAYER_COLORS = [
  "#d92d3c", // red
  "#d4570f", // orange
  "#a97800", // amber
  "#5f8a00", // lime
  "#1f8a3c", // green
  "#00876a", // emerald
  "#00808c", // teal
  "#0a6fb8", // azure
  "#2b55d1", // blue
  "#6a3fd1", // violet
  "#a52bb5", // purple
  "#cf2178", // rose
  "#241e1c", // charcoal
] as const;

export const fonts = {
  display: "'Fredoka Variable', 'Fredoka', ui-rounded, system-ui, sans-serif",
  body: "'Nunito Variable', 'Nunito', ui-rounded, system-ui, sans-serif",
} as const;

export const text = {
  xs: "0.75rem",
  sm: "0.875rem",
  base: "1rem",
  lg: "1.25rem",
  xl: "1.75rem",
  display: "3rem",
} as const;

export const space = {
  1: "0.25rem",
  2: "0.5rem",
  3: "0.75rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  8: "2rem",
} as const;

export const radius = {
  sm: "0.5rem",
  md: "0.875rem",
  lg: "1.25rem",
  xl: "1.75rem",
  full: "999px",
} as const;

export const borderW = "2px";
export const lip = "4px";

export const shadows = {
  card: `0 3px 0 ${colors.lineStrong}, 0 12px 24px -16px rgba(20, 16, 15, 0.35)`,
  float: "0 18px 40px -18px rgba(20, 16, 15, 0.45)",
} as const;

export const motion = {
  fast: "120ms",
  base: "220ms",
  slow: "420ms",
  easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
  easeBounce: "cubic-bezier(0.34, 1.56, 0.64, 1)",
} as const;

export const layers = {
  hud: 10,
  // above the HUD buttons, below open panels (Chat, Board) and MUI modals
  toast: 15,
  panel: 20,
  hand: 5,
} as const;

const kebab = (key: string) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** Every token as a `--name: value;` line, for the :root block. */
export const cssVariables = [
  ...Object.entries(colors).map(([k, v]) => `--${kebab(k)}: ${v};`),
  ...PLAYER_COLORS.map((v, i) => `--player-${i + 1}: ${v};`),
  `--font-display: ${fonts.display};`,
  `--font-body: ${fonts.body};`,
  ...Object.entries(text).map(([k, v]) => `--text-${k}: ${v};`),
  ...Object.entries(space).map(([k, v]) => `--space-${k}: ${v};`),
  ...Object.entries(radius).map(([k, v]) => `--radius-${k}: ${v};`),
  `--border-w: ${borderW};`,
  `--lip: ${lip};`,
  `--shadow-card: ${shadows.card};`,
  `--shadow-float: ${shadows.float};`,
  `--dur-fast: ${motion.fast};`,
  `--dur-base: ${motion.base};`,
  `--dur-slow: ${motion.slow};`,
  `--ease-out: ${motion.easeOut};`,
  `--ease-bounce: ${motion.easeBounce};`,
  // semantic roles: what components actually reference
  "--accent: var(--gold);",
  "--accent-shade: var(--gold-shade);",
  "--accent-ink: var(--ink);",
  "--good: var(--teal-shade);",
  "--good-bg: var(--teal-tint);",
  "--bad: var(--cherry-shade);",
  "--bad-bg: var(--cherry-tint);",
  "--warn-bg: var(--gold-tint);",
  "--info: var(--teal-shade);",
  "--info-bg: var(--teal-tint);",
  "--focus-ring: var(--teal);",
].join("\n");
